import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, Select, Switch, Button, Card, InputNumber } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useNotify } from "../../../context/NotificationContext";
import {
  nonNegativeIntegerValidator,
  noWhitespaceValidator,
} from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";
const { Option } = Select;

function FeatureEdit() {
  const { feature_id } = useParams();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const [languages, setLanguages] = useState([]);
  const navigate = useNavigate();
  const notify = useNotify();
  const getLanguages = async () => {
    const response = await masterService.getVideoLanguages({
      isAll: true,
      status: "Y",
    });
    console.log("Languages:", response);
    const data = response?.data?.data || response?.data || [];
    const list = data.map((item) => ({
      value: item.lang_id,
      label: item.lang_name,
    }));
    setLanguages(list);
  };

  const getFeatureDetails = async () => {
    const response = await masterService.getSingleFeature(feature_id);
    const data = response.data.data;
    form.setFieldsValue(data);
    console.log("Feature Details:", data);
  };

  useEffect(() => {
    setPageTitle(feature_id === "new" ? "Add Feature" : "Edit Feature");
    if (feature_id !== "new") {
      getFeatureDetails();
    }
    getLanguages();
  }, [feature_id, setPageTitle]);

  const onFinish = async (values) => {
    try {
      if (feature_id === "new") {
        const response = await masterService.addFeature(values);
        console.log("Response:", response);
        if (response.data.status) {
          navigate("/features");
        } else {
          notify.error({
            message: response.data.message || "Failed to add feature",
          });
        }
      } else {
        const response = await masterService.updateFeature(feature_id, values);
        console.log("Response:", response);
        if (response.data.status) {
          navigate("/features");
        } else {
          notify.error({
            message: response.data.message || "Failed to update feature",
          });
        }
      }
    } catch (error) {
      console.log("Error:", error);
      notify.error({
        message: error?.data?.message || "Failed to add or update feature",
      });
    }
    console.log("Form Values:", values);
  };

  return (
    <div>
      <Card title="Feature Form" className="">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ is_active: true }}
        >
          {/* Title */}
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Please enter title" },
              { validator: noWhitespaceValidator },
            ]}
          >
            <Input placeholder="Enter feature title" />
          </Form.Item>

          {/* Subtitle */}
          <Form.Item name="subtitle" label="Subtitle">
            <Input placeholder="Enter feature subtitle" />
          </Form.Item>

          <div className="grid grid-cols-3 gap-4">
            {/* Answer Type */}
            <Form.Item
              name="answer_type"
              label="Answer Type"
              rules={[{ required: true, message: "Please select answer type" }]}
            >
              <Select placeholder="Select answer type">
                <Option value="TEXT">Text</Option>
                <Option value="NUMBER">Number</Option>
                <Option value="BOOLEAN">Boolean</Option>
              </Select>
            </Form.Item>

            {/* Order Number */}
            <Form.Item
              name="order_number"
              label="Order Number"
              rules={[nonNegativeIntegerValidator]}
            >
              <InputNumber placeholder="Enter order number" />
            </Form.Item>

            {/* Is Active */}
            <Form.Item
              name="is_active"
              label="Is Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>

          {/* Multilingual Data */}
          <Form.List name="multilingual_data">
            {(fields, { add, remove }) => (
              <>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-semibold">Multilingual Data</h3>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    Add Language Data
                  </Button>
                </div>

                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    className="mb-3 border rounded-md"
                    title={`Language Entry #${name + 1}`}
                    extra={
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ color: "red" }}
                      />
                    }
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "fk_lang_id"]}
                      label="Language"
                      rules={[
                        { required: true, message: "Please enter language" },
                      ]}
                    >
                      <Select
                        placeholder="Select language"
                        options={languages}
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "title"]}
                      label="Translated Title"
                      rules={[
                        {
                          required: true,
                          message: "Please enter translated title",
                        },
                        {
                          validator: noWhitespaceValidator,
                        }
                      ]}
                    >
                      <Input placeholder="Enter translated title" />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "subtitle"]}
                      label="Translated Subtitle"
                      rules={[
                        {
                          validator: noWhitespaceValidator,
                        }
                      ]}
                    >
                      <Input placeholder="Enter translated subtitle" />
                    </Form.Item>
                  </Card>
                ))}
              </>
            )}
          </Form.List>

          {/* Submit */}
          <Form.Item>
            <Button type="primary" htmlType="submit" className="mt-4">
              {feature_id === "new" ? "Create" : "Update"}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default FeatureEdit;
